import axios from 'axios';

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000 // 30초 타임아웃 설정
});

// 토큰 갱신 중 플래그 (중복 요청 방지)
let isRefreshing = false;
let refreshSubscribers = [];

// 토큰 갱신 대기 중인 요청들을 저장하고 갱신 후 재실행
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (token) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

// 토큰 갱신 함수 - 만료된 Access Token을 Refresh Token으로 재발급
const refreshAccessToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      console.warn('Refresh token not found in localStorage');
      throw new Error('No refresh token available');
    }

    console.log('Attempting to refresh access token...');
    const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/auth/refreshtoken`, { refreshToken });

    const { serviceToken, refreshToken: newRefreshToken, user } = response.data;

    // 새 토큰 저장
    localStorage.setItem('serviceToken', serviceToken);

    // refreshToken이 새로 발급된 경우에만 업데이트 (보통은 기존 토큰 재사용)
    if (newRefreshToken && newRefreshToken !== refreshToken) {
      localStorage.setItem('refreshToken', newRefreshToken);
    }

    // 사용자 정보 업데이트
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    }

    console.log('Token refresh successful');
    return serviceToken;
  } catch (error) {
    // Refresh token도 만료되었거나 유효하지 않은 경우
    console.error('Token refresh failed:', error.response?.data?.message || error.message);

    // 로그인 정보 모두 삭제
    localStorage.removeItem('serviceToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    sessionStorage.clear();

    throw error;
  }
};

// Request Interceptor - 모든 요청에 Access Token 자동 추가
instance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('serviceToken');
    if (token) {
      config.headers['x-access-token'] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor - 401 에러 시 자동 토큰 갱신
instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 401 Unauthorized 에러 처리
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // 이미 토큰 갱신 중이면 대기
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token) => {
            originalRequest.headers['x-access-token'] = token;
            resolve(instance(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        // 토큰 갱신 시도
        const newToken = await refreshAccessToken();

        // 갱신 성공 - 대기 중인 모든 요청 재실행
        onTokenRefreshed(newToken);
        isRefreshing = false;

        // 원래 요청 재시도
        originalRequest.headers['x-access-token'] = newToken;
        return instance(originalRequest);
      } catch (refreshError) {
        // Refresh token도 만료 - 로그인 페이지로 이동
        isRefreshing = false;
        refreshSubscribers = [];

        if (!window.location.pathname.includes('/login')) {
          // 사용자에게 세션 만료 알림 (선택사항)
          // alert('세션이 만료되었습니다. 다시 로그인해주세요.');
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    // 네트워크 오류나 타임아웃의 경우 재시도
    if ((error.message === 'Network Error' || error.code === 'ECONNABORTED') && !originalRequest._retry) {
      originalRequest._retry = true;
      return instance(originalRequest);
    }

    return Promise.reject(error);
  }
);

export default instance;
